import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import { IRequestParams, request } from 'helpers/request';
import { requestSagaFactory } from 'helpers';
import {
	mapSchedulingData,
	mapPlannedSurveyData,
} from 'modules/admin-panel/pages/templates/helpers';
import { logSagaErrorMessage } from 'utils';
import { CONSTANTS_TEMPLATES } from 'modules/admin-panel/pages/templates/utils';
import { IPlannedSurveyData } from 'modules/admin-panel/models';
import {
	getCompanyClients,
	getCompanyOffices,
	getSurveyData,
	getSurveyLimits,
	getSurveyRoles,
	postSurveyRole,
	deleteSurveyRole,
	editSurveyRole,
	postSurveyScheduling,
	getPlannedSurveyData,
	getOfficesAndLimitsData,
	updateSurveyScheduling,
} from 'modules/admin-panel/pages/templates/surveys.actions';
import {
	ITemplateSchedulingData,
	ISurveyRoleList,
	ISurveyLimits,
	ISurveyRole,
} from 'modules/admin-panel/pages/templates/models';
import { IClientList } from 'modules/admin-panel/pages/clients/models';

import * as types from 'modules/admin-panel/pages/templates/ActionTypes';
import { getTemplateDataSaga } from 'modules/admin-panel/pages/templates/sagas/templates.saga';

function* getSurveyDataSaga({
	payload: { templateId, clientId },
}: {
	type: string;
	payload: { templateId: string; clientId?: string };
}): any {
	try {
		yield put(getSurveyData.loading());

		const [templateData] = yield all([
			call<(data: { payload: { templateId: string } }) => void>(
				getTemplateDataSaga,
				{ payload: { templateId } },
			),
			call(getSurveyRolesSaga, {}),
		]);

		const companyClients = yield call(getCompanyClientsSaga, {
			payload: { partnerIds: templateData.partners },
		});

		const companyClientId = companyClients && companyClients[0].id;

		if (clientId || companyClientId) {
			yield all([
				call(getCompanyOfficesSaga, {
					payload: { clientId: clientId || companyClientId },
				}),
				call(getSurveyLimitsSaga, {
					payload: { clientId: clientId || companyClientId },
				}),
			]);
		}

		yield put(getSurveyData.success());
	} catch (e) {
		logSagaErrorMessage('getTemplateData', e);
		yield put(getSurveyData.fail(e));
	}
}

function* getOfficesAndLimitsDataSaga({
	payload: { clientId },
}: {
	payload: { clientId: string };
	type: string;
}): any {
	try {
		yield put(getOfficesAndLimitsData.loading());

		const [offices, limits] = yield all([
			call(getCompanyOfficesSaga, { payload: { clientId } }),
			call(getSurveyLimitsSaga, { payload: { clientId } }),
		]);

		yield put(getOfficesAndLimitsData.success({ offices, limits }));
	} catch (e) {
		logSagaErrorMessage('getOfficesAndLimitsData', e);
		yield put(getOfficesAndLimitsData.fail(e));
	}
}

function* getPlannedSurveyDataSaga({
	payload: { surveyId },
}: {
	type: string;
	payload: { surveyId: string };
}) {
	try {
		yield put(getPlannedSurveyData.loading());

		const params: IRequestParams = {
			url: CONSTANTS_TEMPLATES.plannedSurveyUrl(surveyId),
		};

		const { data }: AxiosResponse<IPlannedSurveyData> = yield call(
			request,
			params,
		);

		const { templateId, clientId } = data;

		yield call(getSurveyDataSaga, {
			type: '',
			payload: { templateId: `${templateId}`, clientId },
		});

		yield put(getPlannedSurveyData.success(mapPlannedSurveyData(data)));
	} catch (e) {
		yield put(getPlannedSurveyData.fail());
	}
}

const getCompanyClientsSaga = requestSagaFactory<
	{ partnerIds: string[] },
	IClientList
>({
	url: CONSTANTS_TEMPLATES.companiesUrl,
	actionApi: getCompanyClients,
	logErrorMessage: 'getCompanyClients',
});

const getCompanyOfficesSaga = requestSagaFactory<
	{ clientId: string },
	ITemplateSchedulingData
>({
	url: CONSTANTS_TEMPLATES.officesUrl,
	actionApi: getCompanyOffices,
	logErrorMessage: 'getCompanyOffices',
});

const getSurveyLimitsSaga = requestSagaFactory<
	{ clientId: string },
	ISurveyLimits
>({
	url: CONSTANTS_TEMPLATES.limitsUrl,
	actionApi: getSurveyLimits,
	logErrorMessage: 'getSurveyLimits',
});

const getSurveyRolesSaga = requestSagaFactory<null, ISurveyRoleList>({
	url: CONSTANTS_TEMPLATES.rolesUrl,
	actionApi: getSurveyRoles,
	logErrorMessage: 'getSurveyRoles',
});

function* postSchedulingSaga({
	payload,
}: {
	payload: ITemplateSchedulingData;
	type: string;
}) {
	try {
		yield put(postSurveyScheduling.loading());

		const schedulingData = mapSchedulingData(payload);

		const postSchedulingParams: IRequestParams = {
			method: 'POST',
			url: `${CONSTANTS_TEMPLATES.schedulingUrl}`,
			data: schedulingData,
		};

		const { data }: AxiosResponse<any> = yield call(
			request,
			postSchedulingParams,
		);
		yield put(postSurveyScheduling.success(data));
	} catch (e) {
		logSagaErrorMessage('postScheduling', e);
		yield put(postSurveyScheduling.fail(e));
	}
}

function* updateSchedulingSaga({
	payload,
}: {
	payload: {
		schedulingData: ITemplateSchedulingData;
		surveyId: string;
	};
	type: string;
}) {
	try {
		yield put(updateSurveyScheduling.loading());

		const { schedulingData: formData, surveyId } = payload;
		const schedulingData = mapSchedulingData(formData);

		const postSchedulingParams: IRequestParams = {
			method: 'PATCH',
			url: `${CONSTANTS_TEMPLATES.plannedSurveyUrl(surveyId)}`,
			data: schedulingData,
		};

		const { data }: AxiosResponse<any> = yield call(
			request,
			postSchedulingParams,
		);
		yield put(updateSurveyScheduling.success(data));
	} catch (e) {
		logSagaErrorMessage('updateSurveyScheduling', e);
		yield put(updateSurveyScheduling.fail(e));
	}
}

function* postSurveyRoleSaga({
	payload,
}: {
	payload: { name: string; iconUrl: string };
	type: string;
}) {
	try {
		yield put(postSurveyRole.loading());

		const postSurveyRoleParams: IRequestParams = {
			method: 'POST',
			data: payload,
			url: CONSTANTS_TEMPLATES.rolesUrl,
		};

		const { data }: AxiosResponse<ISurveyRole> = yield call(
			request,
			postSurveyRoleParams,
		);

		yield put(postSurveyRole.success(data));
	} catch (e) {
		logSagaErrorMessage('postSurveyRole', e);
		yield put(postSurveyRole.fail(e));
	}
}

function* deleteSurveyRoleSaga({
	payload,
}: {
	payload: { id: number };
	type: string;
}) {
	try {
		yield put(deleteSurveyRole.loading());

		const deleteSurveyRoleParams: IRequestParams = {
			method: 'DELETE',
			data: payload,
			url: `${CONSTANTS_TEMPLATES.rolesUrl}/${payload}`,
		};

		yield call(request, deleteSurveyRoleParams);

		yield put(deleteSurveyRole.success(payload));
	} catch (e) {
		logSagaErrorMessage('deleteSurveyRole', e);
		yield put(deleteSurveyRole.fail(e));
	}
}

function* editSurveyRoleSaga({
	payload,
}: {
	payload: { id: number; name: string; iconUrl: string };
	type: string;
}) {
	try {
		yield put(editSurveyRole.loading());

		const editSurveyRoleParams: IRequestParams = {
			method: 'PATCH',
			data: payload,
			url: `${CONSTANTS_TEMPLATES.rolesUrl}/${payload.id}`,
		};

		const { data }: AxiosResponse<ISurveyRole> = yield call(
			request,
			editSurveyRoleParams,
		);

		yield put(editSurveyRole.success(data));
	} catch (e) {
		logSagaErrorMessage('editSurveyRole', e);
		yield put(editSurveyRole.fail(e));
	}
}

export function* surveysSaga() {
	yield all([
		takeEvery(types.GET_SURVEY_DATA.DEFAULT, getSurveyDataSaga),
		takeEvery(types.GET_PLANNED_SURVEY_DATA.DEFAULT, getPlannedSurveyDataSaga),
		takeEvery(types.POST_SURVEY_SCHEDULING.DEFAULT, postSchedulingSaga),
		takeEvery(types.UPDATE_SURVEY_SCHEDULING.DEFAULT, updateSchedulingSaga),
		takeEvery(types.GET_SURVEY_LIMITS.DEFAULT, getSurveyLimitsSaga),
		takeEvery(types.GET_COMPANY_OFFICES.DEFAULT, getCompanyOfficesSaga),
		takeLatest(types.GET_COMPANY_CLIENTS.DEFAULT, getCompanyClientsSaga),
		takeEvery(types.GET_SURVEY_ROLES.DEFAULT, getSurveyRolesSaga),
		takeEvery(types.POST_SURVEY_ROLE.DEFAULT, postSurveyRoleSaga),
		takeEvery(types.DELETE_SURVEY_ROLE.DEFAULT, deleteSurveyRoleSaga),
		takeEvery(types.EDIT_SURVEY_ROLE.DEFAULT, editSurveyRoleSaga),
		takeEvery(
			types.GET_OFFICES_AND_LIMITS_DATA.DEFAULT,
			getOfficesAndLimitsDataSaga,
		),
	]);
}
